/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {Alert, Box, CircularProgress, Paper,} from '@mui/material';
import {ResourceUpdateListener, useWorkspaceContext} from '../../../context/workspaceContext';
import {BasicPage} from '../../../@types/spring-data-page';
import {ResourceType} from '../../../@types/resource-type';

// Import the adapted MRT component
import {ExplorerMRT} from './ExplorerMRT';

// Import types
import {
    ExplorerContainerProps, ExplorerContainerRef, ExplorerDatasetType, ExtendedTreeViewItem
} from './config/explorer-mrt-types';

// Import data fetching hook
import {useExplorerData} from './hooks/useExplorerData';
import {useTreeFilter} from './hooks/useTreeFilter';

// Import dataset configuration
import {defaultDataset, isDatasetCreatable} from './config/explorer-dataset-config';

// Import Group utilities
import Group from '../../../@types/group';
import {makeGroup} from '../../../utils/group-utils';

// Import debounce hook
import {useDebounce} from '../../../hooks/useDebounce';

// Import persistence - removed

/**
 * Consolidated Explorer component that replaces multiple separate components
 * with a unified MRT-based implementation.
 */
export const ExplorerContainer = forwardRef<ExplorerContainerRef, ExplorerContainerProps>(({
    workspaceId,
    currentDataset: controlledDataset,
    initialDataset = defaultDataset,
    onDatasetChange,
    onResourceSelect,
    onResourceDoubleClick,
    onEdit,
    onDelete,
    onCopy,
    onAction,
    onOpenIRPWorkbook,
    toolbar,
    groupId,
}, ref) => {
    const {workspaceObjects, subscribeToResourceUpdates, addOrUpdateAdditionalResource, setWorkspaceObjects} = useWorkspaceContext();
    
    // Track processed deletions to prevent circular updates
    const processedDeletionsRef = React.useRef<Set<string>>(new Set());
    
    // Use a ref to store the refetch function to avoid circular dependencies
    const refetchRef = React.useRef<(() => Promise<void>) | null>(null);

    // Dataset state management: This component supports both controlled and uncontrolled modes.
    // Controlled mode: Parent manages dataset via 'controlledDataset' prop and 'onDatasetChange' callback.
    // Uncontrolled mode: Component manages its own state using 'internalDataset' (default).
    // This pattern allows the component to work standalone or be fully controlled by a parent.
    const [internalDataset, setInternalDataset] = useState<ExplorerDatasetType>(initialDataset);
    const currentDataset = controlledDataset || internalDataset;

    // Page state for pagination - default to 10 items per page
    const [page, setPage] = useState<BasicPage>({
        offset: 0, size: 10, direction: 'ASC', sort: 'name',
    });

    // Search and filter state
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState<Record<string, any>>({});

    // Debounce search text to avoid excessive API calls
    const debouncedSearchText = useDebounce(searchText, 300);

    // Determine whether to use server-side or client-side search
    // Most datasets use server-side search for proper pagination support
    // Client-side filtering (useTreeFilter) still runs afterward to maintain tree hierarchy display
    const useServerSideSearch = currentDataset === ResourceType.NEW_OCCURRENCE_REPORTS
        || currentDataset === ResourceType.FOUNDRY_EVENTS
        || currentDataset === ResourceType.USERS
        || currentDataset === ResourceType.SERVICE_USER_ACCOUNTS
        || currentDataset === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS
        || currentDataset === ResourceType.PERFORMANCE_SHAPING_FACTORS
        || currentDataset === ResourceType.GROUPS;

    // Use the data fetching hook for real API calls
    const {data, loading, error, refetch, totalElements, totalPages} = useExplorerData({
        workspaceId,
        dataset: currentDataset,
        groupId: groupId || (workspaceObjects?.objectType === ResourceType.GROUPS ? workspaceObjects.resources?.[0]?.uniqueId : undefined),
        searchText: useServerSideSearch ? debouncedSearchText : '', // Server-side search for flat datasets, client-side for trees
        filters,
        page,
    });
    
    // Store refetch in ref to avoid circular dependencies
    React.useEffect(() => {
        refetchRef.current = refetch;
    }, [refetch]);

    // Reset to first page when debounced search text changes (for server-side search only)
    // IMPORTANT: Only create new page object if offset is actually changing to prevent duplicate fetches
    React.useEffect(() => {
        if (useServerSideSearch && debouncedSearchText !== undefined) {
            setPage((prev: BasicPage) => {
                // Only update if we're not already on the first page
                // Returning same reference prevents unnecessary re-render and duplicate API call
                if (prev.offset !== 0) {
                    return {...prev, offset: 0};
                }
                return prev;
            });
        }
    }, [debouncedSearchText, useServerSideSearch]);

    // Expose the refresh function to parent components via ref
    useImperativeHandle(ref, () => ({
        refresh: (): void => {
            if (refetchRef.current) {
                refetchRef.current();
            }
        }
    }), []);
    
    // Debug logging
    // eslint-disable-next-line no-console
    // console.log('ExplorerContainer state:', {
    //     currentDataset,
    //     workspaceObjectsType: workspaceObjects?.objectType,
    //     controlledDataset,
    //     internalDataset
    // });

    // Apply client-side filtering only for tree datasets (Groups, Safety Models)
    // For flat datasets, filtering happens server-side
    const {filteredData: displayData, matchingIds} = useTreeFilter(
        data,
        useServerSideSearch ? '' : searchText // Only filter client-side for tree datasets
    );


    // Subscribe to resource updates
    useEffect(() => {
        // Subscribe to updates for the current dataset type
        if (!currentDataset) return;
        
        const handleUpdate: ResourceUpdateListener = (event) => {
            if (event.source === 'save' || event.source === 'delete') {
                // For deletions, check if we've already processed this specific event
                if (event.source === 'delete') {
                    const deleteKey = `${event.type}-${event.id}-${event.timestamp}`;
                    if (processedDeletionsRef.current.has(deleteKey)) {
                        return;
                    }
                    processedDeletionsRef.current.add(deleteKey);
                    
                    // Clear the deletion tracking after a delay
                    setTimeout(() => {
                        processedDeletionsRef.current.delete(deleteKey);
                    }, 5000);
                }
                
                // Always refresh to get latest data
                if (refetchRef.current) {
                    refetchRef.current();
                }
            }
        };
        
        // Subscribe to all resource types to catch any updates that might affect the current view
        // This is simpler than trying to figure out all the relationships
        const unsubscribes: (() => void)[] = [];
        
        // Subscribe to common resource types that might affect any view
        const resourceTypesToWatch = [
            ResourceType.GROUPS,
            ResourceType.SAFETY_MODELS,
            ResourceType.EVENT_SEQUENCES,
            // Components share the Safety Models working-area tree, and are also their own row
            // in the Component Catalogue -- either view needs to refetch when one is created,
            // edited or published. ComponentEditor always publishes ResourceType.COMPONENT
            // (never COMPONENT_CATALOGUE, which names a dataset, not a resource kind), so this one
            // entry covers both views.
            ResourceType.COMPONENT,
            ResourceType.WORKSHOPS,
            ResourceType.RISK_FORECASTS,
            ResourceType.OPERATIONAL_CHANGES,
            ResourceType.PERFORMANCE_SHAPING_FACTORS,
            ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
            ResourceType.INTEGRATED_RISK_PICTURES
        ];
        
        resourceTypesToWatch.forEach(resourceType => {
            unsubscribes.push(subscribeToResourceUpdates(resourceType, handleUpdate));
        });
        
        // Also subscribe to the current dataset type if it's not in the list
        if (currentDataset && !resourceTypesToWatch.includes(currentDataset)) {
            unsubscribes.push(subscribeToResourceUpdates(currentDataset, handleUpdate));
        }
        
        // Cleanup all subscriptions on unmount or dataset change
        return (): void => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, [subscribeToResourceUpdates, currentDataset, refetch]);

    // Store groups in additionalResources when they are loaded
    useEffect(() => {
        if (currentDataset === ResourceType.GROUPS && data.length > 0) {
            // Convert the tree data back to flat list for additionalResources
            // Ensure all objects are proper Group instances (some may be plain objects from pathToRoot placeholders)
            const flatGroups: Group[] = [];
            const collectGroups = (items: ExtendedTreeViewItem[]): void => {
                items.forEach(item => {
                    if (item.type === ResourceType.GROUPS && item.object) {
                        // Convert to Group instance if not already one
                        const group = item.object instanceof Group ? item.object : makeGroup(item.object);
                        flatGroups.push(group);
                    }
                    if (item.children) {
                        collectGroups(item.children);
                    }
                });
            };
            
            collectGroups(data);
            
            // Update additionalResources with the groups
            if (flatGroups.length > 0) {
                addOrUpdateAdditionalResource({
                    objectType: ResourceType.GROUPS,
                    resources: flatGroups
                });
                setWorkspaceObjects({ objectType: ResourceType.GROUPS, resources: flatGroups });
            }
        }
    }, [currentDataset, data, setWorkspaceObjects, addOrUpdateAdditionalResource]);

    // Handle dataset change: Updates the current dataset (via parent callback or internal state),
    // then resets all view state (search, filters, pagination) to ensure a clean view of the new dataset.
    const handleDatasetChange = (newDataset: ExplorerDatasetType): void => {
        if (onDatasetChange) {
            onDatasetChange(newDataset);
        } else {
            setInternalDataset(newDataset);
        }
        setSearchText(''); // Clear search
        setFilters({}); // Clear filters
        setPage((prev: BasicPage) => ({
            ...prev,
            offset: 0,
            size: newDataset === ResourceType.FOUNDRY_EVENTS ? 25 : 10,
        }));
    };

    // Handle search change: Updates search text
    // For server-side search, page reset happens when debouncedSearchText changes (see useEffect above)
    // For client-side search (trees), reset page immediately since there's no debounce delay
    const handleSearchChange = (value: string): void => {
        setSearchText(value);
        if (!useServerSideSearch) {
            setPage((prev: BasicPage) => ({...prev, offset: 0})); // Reset to first page for client-side filtering
        }
    };

    // Handle filter change: Updates a specific filter and resets pagination to display filtered results from start.
    const handleFilterChange = (filterKey: string, value: any): void => {
        setFilters(prev => ({
            ...prev, [filterKey]: value,
        }));
        setPage((prev: BasicPage) => ({...prev, offset: 0})); // Reset to first page
    };

    // Handle page change: Navigates to a specific page while maintaining current page size and filters.
    // Note: newPage here is actually the offset value (record index), not page index
    const handlePageChange = (newOffset: number): void => {
        setPage((prev: BasicPage) => ({
            ...prev, offset: newOffset,
        }));
    };

    // Handle page size change: Updates items per page and returns to first page to show resized results.
    const handlePageSizeChange = (newSize: number): void => {
        setPage((prev: BasicPage) => ({
            ...prev, size: newSize, offset: 0, // Reset to first page
        }));
    };

    // Handle create: Triggers the parent-provided create callback when toolbar's create button is clicked.
    const handleCreate = (): void => {
        if (toolbar?.onCreateClick) {
            toolbar.onCreateClick();
        }
    };

    // Handle edit: Delegates to parent's edit handler to open the resource for editing.
    const handleEdit = (resource: ExtendedTreeViewItem): void => {
        if (onEdit) {
            onEdit(resource);
        }
    };

    // Handle delete: Delegates to parent's delete handler to remove the selected resource.
    const handleDelete = (resource: ExtendedTreeViewItem): void => {
        if (onDelete) {
            onDelete(resource);
        }
    };

    // Handle copy: Delegates to parent's copy handler to duplicate the selected resource.
    const handleCopy = (resource: ExtendedTreeViewItem): void => {
        if (onCopy) {
            onCopy(resource);
        }
    };

    // Handle row click: Notifies parent that a resource was selected (single-click selection).
    const handleRowClick = (resource: ExtendedTreeViewItem): void => {
        // Group rows in a tree view are expanded/collapsed by their button; clicking the row does nothing
        if (resource.type === ResourceType.GROUPS) {
            return;
        }

        if (resource.type === ResourceType.INTEGRATED_RISK_PICTURES) {
            if (resource.object?.status === 'COMPLETED' && onAction) {
                onAction('view', resource);
            } else if (onAction) {
                onAction('properties', resource);
            }
            return;
        }

        if (onResourceSelect) {
            onResourceSelect(resource);
        }
    };

    // Handle row double-click: Triggers parent's double-click handler to open/activate the resource.
    const handleRowDoubleClick = (resource: ExtendedTreeViewItem): void => {
        if (onResourceDoubleClick) {
            onResourceDoubleClick(resource);
        }
    };

    // Render loading state
    if (loading && displayData.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            {/* Error display */}
            {error && (
                <Alert severity="error" sx={{m: 2}}>
                    {error}
                </Alert>
            )}

            {/* Main content */}
            {/* overflow hidden + minHeight 0 lets the MRT table panel own the scroll internally,
                keeping the toolbar (dataset selector) and column headers fixed above it */}
            <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
                <ExplorerMRT
                    data={displayData}
                    loading={loading}
                    currentDataset={currentDataset}
                    searchText={searchText}
                    filters={filters}
                    matchingIds={matchingIds}
                    page={{
                        ...page,
                        totalElements: totalElements || 0,
                        totalPages: totalPages || 0,
                    } as any}
                    onDatasetChange={handleDatasetChange}
                    onSearchChange={handleSearchChange}
                    onFilterChange={handleFilterChange}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onRowClick={handleRowClick}
                    onRowDoubleClick={handleRowDoubleClick}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onCopy={handleCopy}
                    onAction={onAction}
                    onRefresh={refetch}
                    onOpenIRPWorkbook={onOpenIRPWorkbook}
                    toolbar={toolbar ? {
                        ...toolbar,
                        showCreateButton: toolbar.showCreateButton && isDatasetCreatable(currentDataset),
                        onCreateClick: toolbar.onCreateClick ?? handleCreate,
                        // Foundry Events supports importing events by source (handled in ExplorerMRT)
                        showImportButton: toolbar.showImportButton || currentDataset === ResourceType.FOUNDRY_EVENTS,
                    } : {
                        showCreateButton: isDatasetCreatable(currentDataset),
                        onCreateClick: handleCreate,
                        createButtonText: `New ${currentDataset.slice(0, -1)}`, // Remove 's' from plural
                        showImportButton: currentDataset === ResourceType.FOUNDRY_EVENTS,
                    }}
                />
            </Box>
        </Paper>
    );
});

ExplorerContainer.displayName = 'ExplorerContainer';

export default ExplorerContainer;